import { NewButton, NewButtonVariant } from "@visiblethread/react-components";
import {
  getOutlineTypeLabel,
  PHASE_BY_EVENT,
} from "constants/outlineGeneration";
import { Star } from "lucide-react";
import { func } from "prop-types";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { showErrorModal } from "slices/app/actions/error";
import {
  selectClarification,
  selectGateState,
  selectOutlineDocuments,
} from "slices/proposalOutline/selectors";
import {
  mergeAnswersLastWins,
  safeJsonParse,
} from "slices/proposalOutline/utils";

import PhaseStepper from "../components/OutlineGenerationProgress/PhaseStepper";
import gateStyles from "./Gate.module.scss";
import PlannerBlock, {
  DOCUMENT_TYPE_DIMENSION,
  RE_ASSESS_OPTION_ID,
} from "./PlannerBlock";
import styles from "./TypeReviewGate.module.scss";

const ERROR_TITLE = "There was a problem submitting your answers.";
const GATE_TYPE_REVIEW = "TYPE_REVIEW";

// Short "how I'll read this" blurbs per detected type key. Mirrors the coarse
// taxonomy in OutlineDocumentType.java (backend). Unmapped/unknown keys fall
// back to a generic blurb built from the document's label.
const DOC_TYPE_BLURBS = {
  US_FEDERAL_RFP:
    "I'll read this as a US Federal RFP — expect Section L/M instructions, FAR/DFARS clauses, and a formal SOW/PWS.",
  COMMERCIAL_RFP:
    "I'll read this as a Commercial RFP — expect vendor requirements, evaluation criteria, and pricing/terms sections.",
  EU_UK_TENDER:
    "I'll read this as an EU / UK Tender — expect ITT/PQQ structure, award criteria, and framework/lot terms.",
  GRANT_APPLICATION:
    "I'll read this as a Grant Application — expect funding guidelines, eligibility criteria, and reporting requirements.",
  HEALTHCARE:
    "I'll read this as a Healthcare document — expect clinical, compliance, and regulatory requirements.",
  BROAD_AGENCY_ANNOUNCEMENT:
    "I'll read this as a Broad Agency Announcement — expect research topic areas, white paper/full-proposal instructions, and evaluation factors.",
};

const buildSummary = (docTypeKey, docTypeLabel) =>
  DOC_TYPE_BLURBS[docTypeKey] ||
  `I'll read this as a ${
    docTypeLabel || "general"
  } document and extract requirements accordingly.`;

// The archetype the completed outline's SmartBar will show, rendered through the same mapping it
// uses. Without it the gate names only the AI's free-form characterization, so the detected type
// appears to change once generation finishes. Null when there is no archetype to name (a dynamic
// document) or when the free-form label already is it.
const buildArchetypeLabel = (docTypeKey, docTypeLabel) => {
  if (!docTypeKey) {
    return null;
  }
  const archetypeLabel = getOutlineTypeLabel(docTypeKey);
  return archetypeLabel === docTypeLabel ? null : archetypeLabel;
};

const buildConfidenceLabel = (confidence) => {
  if (typeof confidence !== "number") {
    return null;
  }
  return `CONFIDENCE ${Math.round(confidence * 100)}%`;
};

// Gate 1 (AWAITING_TYPE_REVIEW): a card per detected outline document, plus a
// planner block (Task 7/8's shared PlannerBlock) rendered below the cards
// whenever the gate has an open clarification round. Answering a round submits
// to submitTypeAnswers; a truthy followUpBlock in the response renders the next
// round in its place. One-action gate: when the planner returns no follow-up,
// answering IS the confirm and the gate advances automatically. The standalone
// Confirm button is shown only when there's no question (high-confidence accept).
const TypeReviewGate = ({ onConfirm, onSubmitAnswers }) => {
  const dispatch = useDispatch();
  const documents = useSelector(selectOutlineDocuments);
  const clarification = useSelector(selectClarification);
  const gateState = useSelector(selectGateState);

  // On a page reload, SSE replay is NOT guaranteed to re-deliver the
  // type_review_ready event — selectClarification (SSE-only) can come back
  // empty even though a round is genuinely still open. Fall back to the gate's
  // persisted block from the GET (selectGateState's `block`, a stringified
  // OutlineClarificationBlock) so the question doesn't silently vanish. Guarded
  // by gate type so a stale/other-gate's persisted block is never misapplied.
  // SSE-driven `clarification` still wins whenever it's present.
  const persistedBlock = useMemo(() => {
    if (gateState?.gate !== GATE_TYPE_REVIEW) {
      return null;
    }
    return safeJsonParse(gateState?.block);
  }, [gateState]);

  const initialBlock = clarification || persistedBlock;
  const [block, setBlock] = useState(initialBlock);
  // Answers submitted across THIS gate's own rounds, accumulated locally. Redux's
  // clarificationAnswers accumulates across the whole outline run (including an
  // unrelated AWAITING_CLARIFICATION round via resumeOutline), so reusing it here
  // as priorAnswers could wrongly prefill a stale, unrelated answer that happens to
  // share a questionId. submitGateAnswers doesn't dispatch into that Redux slice,
  // so this gate must track its own prefill state instead.
  const [submittedAnswers, setSubmittedAnswers] = useState([]);

  // A fresh clarification block can land in Redux after mount (e.g. the gate becomes ready again
  // on SSE reconnect/replay) — resync local state so the gate doesn't keep rendering a stale
  // round. Keyed on the block's IDENTITY, not the object: every reconnect replays type_review_ready
  // and handleGateReady dispatches a newly-parsed object, so keying on initialBlock itself reset a
  // user who was mid-answer back to round 1 — and PlannerBlock, keyed on blockId, remounted and
  // lost what they had typed. A network blip or the 90s stall watchdog was enough to trigger it.
  const initialBlockRef = useRef(initialBlock);
  initialBlockRef.current = initialBlock;
  const initialBlockId = initialBlock?.blockId ?? null;
  const initialRound = initialBlock?.round ?? null;

  useEffect(() => {
    setBlock(initialBlockRef.current);
    setSubmittedAnswers([]);
  }, [initialBlockId, initialRound]);

  const handleSubmit = async (answers) => {
    try {
      const isReAssess = answers.some(
        (a) => a.optionId === RE_ASSESS_OPTION_ID
      );
      const response = await onSubmitAnswers(answers);
      const followUp = response?.followUpBlock
        ? JSON.parse(response.followUpBlock)
        : null;
      setSubmittedAnswers((prev) => mergeAnswersLastWins(prev, answers));

      // A follow-up that only re-asks the document type the user just answered
      // adds nothing — the planner re-asks it every round for low-confidence /
      // dynamic docs, which reads as "nothing happened". Treat it as resolved.
      const followUpOnlyReasksType =
        !!followUp &&
        followUp.questions?.length > 0 &&
        followUp.questions.every(
          (q) => q.dimension === DOCUMENT_TYPE_DIMENSION
        );

      // One-action gate: an explicit answer IS the confirmation — advance rather
      // than making the user click a separate Confirm. Re-Assess is exempt (it
      // deliberately re-reads and re-asks); a genuinely different follow-up
      // question is shown for answering.
      if (isReAssess) {
        setBlock(followUp);
        if (!followUp) {
          await onConfirm();
        }
      } else if (!followUp || followUpOnlyReasksType) {
        setBlock(null);
        await onConfirm();
      } else {
        setBlock(followUp);
      }
    } catch (error) {
      dispatch(showErrorModal(error, ERROR_TITLE));
    }
  };

  return (
    <div className={gateStyles.gate} data-testid="type-review-gate">
      <PhaseStepper
        currentPhase={PHASE_BY_EVENT.type_review_ready}
        failed={false}
        awaitingGate
      />

      <h3>Confirm Document Types</h3>

      <div className={styles.cards}>
        {documents.map((doc) => {
          const confidenceLabel = buildConfidenceLabel(doc.confidence);
          const archetypeLabel = buildArchetypeLabel(
            doc.docTypeKey,
            doc.docTypeLabel
          );
          return (
            <div key={doc.nextgenDocumentId ?? doc.id} className={styles.card}>
              <div className={styles.cardHeader}>
                <span className={styles.docTypeName}>
                  <span className={styles.docTypeLabel}>
                    {doc.docTypeLabel}
                  </span>
                  {archetypeLabel && (
                    <span className={styles.archetypeLabel}>
                      {archetypeLabel}
                    </span>
                  )}
                </span>
                {doc.isPrimary && (
                  <span className={styles.primaryMarker}>
                    <Star size={14} aria-hidden="true" />
                    Primary Document
                  </span>
                )}
              </div>
              {confidenceLabel && (
                <span className={styles.chip}>{confidenceLabel}</span>
              )}
              <p className={styles.summary}>
                {buildSummary(doc.docTypeKey, doc.docTypeLabel)}
              </p>
            </div>
          );
        })}
      </div>

      {block && (
        <div className={styles.plannerRound}>
          <PlannerBlock
            key={block.blockId}
            block={block}
            priorAnswers={submittedAnswers}
            onSubmit={handleSubmit}
          />
        </div>
      )}

      {/* With a question shown, answering it advances the gate (see handleSubmit),
          so the standalone Confirm is only needed for the no-question path where
          the user simply accepts the detected document types. */}
      {!block && (
        <NewButton onClick={() => onConfirm()} variant={NewButtonVariant.l1}>
          Confirm Document Types
        </NewButton>
      )}
    </div>
  );
};

TypeReviewGate.propTypes = {
  onConfirm: func.isRequired,
  onSubmitAnswers: func.isRequired,
};

export default TypeReviewGate;
